<template>
	<div>
		<users-update-submenu />

		<core-layout-content full>
			<!-- Headline -->
			<core-layout-headline-and-actions>
				<template slot="headline">{{ $t("inherited_logins") }}</template>
				<template slot="actions">
					<j-button
						v-can:and="['writeLogins']"
						@click="$modal.show('addInheritedLogin')"
					>
						<crm-icon name="add" size="small" color="c5" />
						{{ $t("add") }}
					</j-button>
				</template>
			</core-layout-headline-and-actions>

			<!-- Table -->
			<j-card class="mt4">
				<j-data-table
					:header="computedHeader"
					:list="computedList"
					:sortable="false"
					:isLoading="isLoading"
					@sort="onSort($event)"
				>
					<template slot="list" slot-scope="{ item }">
						<td v-for="(column, i) in computedHeader" :key="i">
							<!-- Link -->
							<template v-if="i === 0">
								<router-link
									:to="{
											name: 'UsersUpdate',
											params: { loginId: item.id }
										}"
								>{{ item.name }}</router-link>
							</template>
							<!-- Others -->
							<template v-else>{{ item[column.field] }}</template>

							<!-- Actions Field -->
							<template v-if="column.field === 'actions'">
								<div class="action">
									<!-- Delete -->
									<crm-icon
										name="delete"
										size="normal"
										color="cError"
										@click.native="removeInheritedLogins(item.id)"
										v-can="'writePermissionGroups'"
									/>
								</div>
							</template>
						</td>
					</template>
					<template slot="empty">{{ $t("errors.no_data") }}</template>
				</j-data-table>
			</j-card>
		</core-layout-content>

		<!-- Add modal -->
		<core-modal name="addInheritedLogin" :headline="$t('add_inherited_logins')">
			<div class="p4">
				<core-form-input
					componentName="j-base-select"
					:label="$t('select_user')"
					v-model="selectedLogin"
					:options="logins"
				/>
				<j-button class="mt4" @click="addInheritedLogin">{{$t("add")}}</j-button>
			</div>
		</core-modal>
	</div>
</template>

<script>
import UsersUpdateSubmenu from "./UsersUpdateSubmenu"
import CoreLayoutContent from "@/modules/core/components/CoreLayoutContent"
import JButton from "@/components/JButton"
import CoreLayoutHeadlineAndActions from "@/modules/core/components/CoreLayoutHeadlineAndActions"
import JDataTable from "@/components/JDataTable/"
import JCard from "@/components/JCard/"
import CrmIcon from "@/components/CrmIcon/"
import CoreModal from "@/modules/core/components/CoreModal"
import CoreFormInput from "@/modules/core/components/CoreFormInput"

import dayjs from "dayjs"
import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"

export default {
	components: {
		UsersUpdateSubmenu,
		CoreLayoutContent,
		JButton,
		CoreLayoutHeadlineAndActions,
		JDataTable,
		JCard,
		CrmIcon,
		CoreModal,
		CoreFormInput
	},

	data() {
		return {
			list: [],
			logins: [],
			selectedLogin: null,
			isLoading: false
		}
	},

	methods: {
		async fetchInheritedLogins() {
			this.isLoading = true
			const query = gql`
				query fetchInheritedLogins($ids: [String]) {
					logins(ids: $ids) {
						totalCount
						list {
							inheritedLogins {
								id
								permissionGroups {
									name
								}
								associatedUser {
									id
									name
									internalCode
								}
								email
								isEmailConfirmed
								createdAt
							}
						}
					}
				}
			`

			const variables = {
				ids: [this.$route.params.loginId]
			}
			const res = await api({ query, variables })
			this.isLoading = false

			// Check errors
			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
				return
			}

			// Assign
			this.list = res?.data?.logins?.list[0]?.inheritedLogins
		},

		async fetchLogins() {
			this.isLoading = true
			const query = gql`
				query fetchLogins {
					logins {
						totalCount
						list {
							id
							associatedUser {
								id
								name
								internalCode
							}
							email
						}
					}
				}
			`

			const variables = {}

			const res = await api({ query, variables })
			this.isLoading = false

			// Check errors
			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
				return
			}

			// Assign
			this.logins = res?.data?.logins?.list
				?.filter(
					item =>
						item?.email !== "admin@covergo.com" &&
						item.id !== this.$route.params.loginId
				)
				?.map(item => ({
					name: `${item?.associatedUser?.name} - ${item?.email}`,
					value: item?.id
				}))
		},

		async addInheritedLogin() {
			this.isLoading = true
			const query = gql`
				mutation addInheritedLoginToLogin(
					$loginId: String!
					$loginIdToInheritFrom: String!
				) {
					addInheritedLoginToLogin(
						loginId: $loginId
						loginIdToInheritFrom: $loginIdToInheritFrom
					) {
						status
						errors
						errors_2 {
							code
							message
						}
					}
				}
			`

			const variables = {
				loginId: this.$route.params.loginId,
				loginIdToInheritFrom: this.selectedLogin
			}

			const res = await api({ query, variables })

			if (res?.data?.addInheritedLoginToLogin?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.errors ||
							res?.data?.addInheritedLoginToLogin?.errors ||
							res?.data?.addInheritedLoginToLogin?.errors_2?.message
					}
				})

				return
			}

			this.isLoading = false

			this.fetchInheritedLogins()
			this.$modal.hide("addInheritedLogin")
			this.selectedLogin = null
			this.$store.dispatch("addMessage", {
				message: {
					type: "success",
					text: this.$t("added_inherited_login")
				}
			})
		},

		async removeInheritedLogins(id) {
			const query = gql`
				mutation removeInheritedLoginFromLogin(
					$loginId: String!
					$loginIdToStopInheritFrom: String!
				) {
					removeInheritedLoginFromLogin(
						loginId: $loginId
						loginIdToStopInheritFrom: $loginIdToStopInheritFrom
					) {
						status
						errors
						errors_2 {
							code
							message
						}
					}
				}
			`

			const variables = {
				loginId: this.$route.params.loginId,
				loginIdToStopInheritFrom: id
			}

			const res = await api({ query, variables })

			if (res?.data?.removeInheritedLoginFromLogin?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.errors ||
							res?.data?.removeInheritedLoginFromLogin?.errors ||
							res?.data?.removeInheritedLoginFromLogin?.errors_2?.message
					}
				})

				return
			}

			this.isLoading = false

			this.fetchInheritedLogins()
			this.$store.dispatch("addMessage", {
				message: {
					type: "success",
					text: this.$t("removed_inherited_login")
				}
			})
		}
	},

	computed: {
		computedHeader() {
			return [
				{
					field: "name",
					text: this.$t("name")
				},
				{
					field: "email",
					text: this.$t("email_or_username")
				},
				// {
				// 	text: this.$t("code"),
				// 	field: "internalCode"
				// },
				{
					text: this.$t("permission_groups"),
					field: "permissionGroups"
				},
				// {
				// 	text: this.$t("status"),
				// 	field: "status"
				// },
				{
					text: this.$t("created"),
					field: "createdAt"
				},
				{
					text: this.$t("actions"),
					field: "actions"
				}
			]
		},

		computedList() {
			return this.list.map(item => ({
				id: item.id, // id of login
				name: item?.associatedUser?.name || "---",
				email: item.email,
				internalCode: item?.associatedUser?.internalCode,
				isEmailConfirmed: item?.isEmailConfirmed,
				associatedUser: item?.associatedUser,
				permissionGroups: item?.permissionGroups
					?.map(item => item?.name)
					.join(","),
				status: item?.isEmailConfirmed
					? this.$t("active")
					: this.$t("pending_invitation"),
				createdAt: item.createdAt
					? dayjs(item.createdAt).format(
							this.$store.state.settings.dateTimeFormat
					  )
					: null
			}))
		}
	},

	created() {
		this.fetchInheritedLogins()
		this.fetchLogins()
	}
}
</script>

